import { ProductComparisonResult } from '@public-api-contracts/models/assistant/ProductComparisonTable';
import { Product } from '../../../models/Product';
import './ProductsComparisonTable.less';
import { ComparingProduct } from '../../../models/ComparingProduct';
import { useCallback, useMemo, useState } from 'preact/hooks';
import { Ref } from 'preact/compat';
import ProductImageCarousel from '../../product/image-carousel/ProductImageCarousel';
import IconButton from '../../button/IconButton';
import ProductRating from '../../product/ProductRating';
import ProductPrice from '../../product/ProductPrice';
import Button from '../../button/Button';
import Icon from '../../icon/Icon';
import Chip from '../../chip/Chip';
import { useTranslation } from '../../../hooks/useTranslation';
import useAppDispatch from '../../../hooks/useAppDispatch';

type Section = {
    title: string;
    attributes: {
        title: string;
        values: (string | null)[];
    }[];
};

export type ProductsComparisonTableProps = {
    products: Product[];
    groups: ProductComparisonResult['groups'];
    suggestedProductsQuantity?: number;
    onRemoveProduct?: (productId: string) => void;
    onAddProductToCart: (product: Product) => void;
    onClose?: () => void;
    mainDataRef?: Ref<HTMLElement>;
    expandAllGroups?: boolean;
    showExpandButtons?: boolean;
    minSectionsToShow?: number;
};

const ProductsComparisonTable = ({
    products,
    groups,
    suggestedProductsQuantity = 0,
    onRemoveProduct,
    onAddProductToCart,
    onClose,
    mainDataRef,
    expandAllGroups = false,
    showExpandButtons = true,
    minSectionsToShow = 3
}: ProductsComparisonTableProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [expandedGroups, setExpandedGroups] = useState(
        expandAllGroups
            ? groups.reduce(
                  (previous, current) => {
                      previous[current.group] = true;
                      return previous;
                  },
                  {} as Record<string, boolean>
              )
            : {}
    );

    const comparingProducts: ComparingProduct[] = useMemo(() => {
        if (products.length === 0) {
            return [];
        }
        return products.map(product => ({
            ...product,
            extraFields: groups.map(group => ({
                group: group.group,
                attributes: group.attributes.map(attribute => ({
                    name: attribute.name,
                    value:
                        attribute.values.find(
                            value => value.productId === product.productId
                        )?.value || ''
                }))
            }))
        }));
    }, [products, groups]);

    const selectedProductsQuantity =
        products.length - suggestedProductsQuantity;

    const getProductMainData = useCallback(
        (product: Product, index: number) => {
            return (
                <>
                    <ProductImageCarousel
                        onClick={() =>
                            dispatch.featureState.startFeature({
                                featureType: 'product-detail',
                                productId: product.productId,
                                product,
                                source: 'brilliant'
                            })
                        }
                        product={product}
                        chips={
                            index + 1 > selectedProductsQuantity ? (
                                <Chip variant="white-special" size="small">
                                    {t('SUGGESTED_PRODUCT_BADGE')}
                                </Chip>
                            ) : undefined
                        }
                    >
                        {products.length > 1 && onRemoveProduct ? (
                            <div className="bri-ProductsComparisonTable-remove-button-container">
                                <IconButton
                                    className="bri-ProductsComparisonTable-remove-button"
                                    variant="light"
                                    iconId="close"
                                    size="x-small"
                                    onClick={event => {
                                        event.stopPropagation();
                                        onRemoveProduct(product.productId);
                                    }}
                                />
                            </div>
                        ) : null}
                    </ProductImageCarousel>
                    <ProductRating
                        ratingCount={product.ratingCount}
                        ratingValue={product.ratingValue}
                    />
                    <span className={`bri-BodyFont`} ref={mainDataRef}>
                        {product.name}
                    </span>
                    <ProductPrice
                        className="bri-TitleBoldFont"
                        product={product}
                    />
                    <Button
                        disabled={!product.available}
                        variant="magic"
                        fullWidth
                        size="medium"
                        onClick={() => onAddProductToCart(product)}
                    >
                        {product.available
                            ? t('ADD_TO_CART_BUTTON_LABEL_ONE')
                            : t('SOLD_OUT_BUTTON_LABEL')}
                    </Button>
                </>
            );
        },
        [onAddProductToCart, onRemoveProduct]
    );

    const getSectionCells = useCallback(
        (section: Section) => {
            return section.attributes.map(({ title, values }, index) =>
                values.map(value => {
                    if (!expandedGroups[section.title] && index > 1) {
                        return <div style={{ display: 'none' }}></div>;
                    }
                    if (value === null) {
                        return <div>&nbsp;</div>;
                    }
                    return (
                        <div className="bri-ProductsComparisonTable-attribute">
                            <div className="bri-Body2Font bri-ProductsComparisonTable-attribute-title">
                                {title}
                            </div>
                            <div className="bri-BodyFont">{value}</div>
                        </div>
                    );
                })
            );
        },
        [expandedGroups]
    );

    return (
        <div
            className={`bri-ProductsComparisonTable`}
            style={{
                '--products-comparison-table-grid-columns': Math.max(
                    3,
                    products.length
                )
            }}
        >
            <div className={'bri-ProductsComparisonTable-grid'}>
                {products.map(getProductMainData)}
                {products.length <= 2 && (
                    <div
                        className={`bri-ProductsComparisonTable-empty-product`}
                        onClick={() => onClose && onClose()}
                    >
                        <Icon iconId="plus" size="x-large" />
                    </div>
                )}
            </div>

            <div className="bri-ProductsComparisonTable-attributes-grid">
                <div className="bri-DisplayFont bri-ProductsComparisonTable-group-title">
                    {t('PRODUCT_DESCRIPTION')}
                </div>
                {products.map(product => (
                    <div className="bri-ProductsComparisonTable-attribute">
                        <div className="bri-Body2Font bri-ProductsComparisonTable-attribute-title">
                            {product.localizedDescription ??
                                t('PRODUCT_NO_DESCRIPTION')}
                        </div>
                    </div>
                ))}
            </div>

            {generateSectionsFromProducts(
                comparingProducts,
                minSectionsToShow
            ).map(section => (
                <div className="bri-ProductsComparisonTable-attributes-grid">
                    <div className="bri-DisplayFont bri-ProductsComparisonTable-group-title">
                        {section.title}
                    </div>
                    {getSectionCells(section)}
                    {showExpandButtons &&
                        !expandedGroups[section.title] &&
                        section.attributes.length > 2 && (
                            <div
                                className={
                                    'bri-ProductsComparisonTable-group-toggle'
                                }
                            >
                                <Button
                                    variant="link"
                                    fullWidth
                                    onClick={() =>
                                        setExpandedGroups({
                                            ...expandedGroups,
                                            [section.title]: true
                                        })
                                    }
                                >
                                    {t('EXPAND_TO_SHOW_MORE')}
                                </Button>
                            </div>
                        )}
                    {showExpandButtons && expandedGroups[section.title] && (
                        <div
                            className={
                                'bri-ProductsComparisonTable-group-toggle'
                            }
                        >
                            <Button
                                variant="link"
                                fullWidth
                                onClick={() =>
                                    setExpandedGroups({
                                        ...expandedGroups,
                                        [section.title]: false
                                    })
                                }
                            >
                                {t('COLLAPSE_TO_SHOW_LESS')}
                            </Button>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default ProductsComparisonTable;

function generateSectionsFromProducts(
    products: ComparingProduct[],
    minSectionsToShow: number
): Section[] {
    const firstProduct = products[0];
    return firstProduct.extraFields.map(field => ({
        title: field.group,
        attributes: field.attributes.map(attribute => ({
            title: attribute.name,
            values: (products as (ComparingProduct | null)[])
                .concat(Array.from(Array(minSectionsToShow)).map(() => null))
                .slice(0, minSectionsToShow)
                .map(p => getExtraFieldValue(p, field.group, attribute.name))
        }))
    }));
}

function getExtraFieldValue(
    product: ComparingProduct | null,
    group: string,
    attribute: string
): string | null {
    if (!product) {
        return null;
    }
    return (
        product.extraFields
            .find(f => f.group === group)
            ?.attributes.find(a => a.name === attribute)?.value || ''
    );
}
